import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { Group, Map, Scene, TourData } from "../../../Types";
import { UseAssetsQuery, UseDownloadAsset } from "../../../api/calls/Queries";
import MapPin from "../../Icons/MapPin";
import Draggable from "react-draggable";
import { FolderIcon, Trashbin } from "../../Icons/gui-elements-icons";
import { getLanguageFile } from "../../common/GetLanguageFile";
import { useAssetListStore } from "../../../api/Zustand";

export default function MapDetails(props: {
  tourID: number;
  selected: number | undefined;
  tourData: TourData;
}) {
  const { setOpenForMap } = useAssetListStore();

  const [size, setSize] = React.useState<{
    w: number;
    h: number;
  }>({
    w: 0,
    h: 0,
  });

  const ref = React.useRef<HTMLDivElement>(null);
  const ref2 = React.useRef<HTMLImageElement>(null);

  const { data: imageList } = UseAssetsQuery(props.tourID, 2);
  const queryClient = useQueryClient();

  const currentMap: Map | undefined = React.useMemo(() => {
    return props.tourData.maps?.find((one) => one.id === props.selected);
  }, [props.tourData.maps, props.selected]);

  const { data: imageBlob } = UseDownloadAsset(
    imageList?.find((one) => one.assetId === currentMap?.assetId)?.assetKeyFull
  );

  const onTitleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      queryClient.setQueryData(["GetTour", props.tourID], {
        ...props.tourData,
        maps: props.tourData.maps?.map((map) =>
          map.id === props.selected ? { ...map, title: e.target.value } : map
        ),
      });
    },
    [queryClient, props.tourData, props.tourID, props.selected]
  );

  const onPinAdd = React.useCallback(
    (target: number) => {
      queryClient.setQueryData(["GetTour", props.tourID], {
        ...props.tourData,
        maps: props.tourData.maps?.map((map) =>
          map.id === props.selected
            ? {
                ...map,
                points: [
                  ...(map.points || []),
                  {
                    target: target,
                    pos: { x: 0.5, y: 0.5 },
                  },
                ],
              }
            : map
        ),
      });
    },
    [queryClient, props.tourData, props.tourID, props.selected]
  );

  const onPinMove = React.useCallback(
    (pos: { x: number; y: number }, target: string | number) => {
      queryClient.setQueryData(["GetTour", props.tourID], {
        ...props.tourData,
        maps: props.tourData.maps?.map((map) =>
          map.id === props.selected
            ? {
                ...map,
                points: map.points?.map((point) =>
                  point.target === target ? { ...point, pos: pos } : point
                ),
              }
            : map
        ),
      });
    },
    [props.selected, props.tourData, props.tourID, queryClient]
  );

  const onRemove = React.useCallback(
    (target: number) => {
      queryClient.setQueryData(["GetTour", props.tourID], {
        ...props.tourData,
        maps: props.tourData.maps?.map((map) =>
          map.id === props.selected
            ? {
                ...map,
                points: map.points?.filter(
                  (points) => points.target !== target
                ),
              }
            : map
        ),
      });
    },
    [props.tourID, props.selected, props.tourData, queryClient]
  );

  React.useEffect(() => {
    if (!ref2.current) return;

    const resizeObserver = new ResizeObserver((e) => {
      const target: HTMLDivElement = e[0].target as HTMLDivElement;

      setSize({
        w: target?.offsetWidth,
        h: target?.offsetHeight,
      });
    });

    resizeObserver.observe(ref2.current);

    setSize({
      w: ref2?.current.offsetWidth,
      h: ref2?.current.offsetHeight,
    });
  }, [ref2]);

  return (
    <div style={{ display: "flex", height: "100%", gap: "1rem" }}>
      <div
        style={{
          height: "100%",
          flex: 8,
          boxSizing: "border-box",
          overflow: "auto",

          position: "relative",
        }}
      >
        <img
          ref={ref2}
          src={imageBlob}
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%,-50%)",
            pointerEvents: "none",
            userSelect: "none",
          }}
          alt=""
        />
        {size.w !== 0 && size.h !== 0 && (
          <div
            className="pin-wrapper"
            ref={ref}
            style={{
              height: size.h,
              width: size.w,
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%,-50%)",
            }}
          >
            {size.h &&
              currentMap?.points?.map((point) => (
                <Draggable
                  onStop={(e, u) => {
                    const y = (u.y + 15) / (size.h || 1);
                    const x = (u.x + 15) / (size.w || 1);

                    const pos = {
                      x: Math.round(x * 10000) / 10000,
                      y: Math.round(y * 10000) / 10000,
                    };
                    // const pos = { x: 0.67, y: 0.33 };

                    onPinMove(pos, point.target);
                  }}
                  key={point.target}
                  bounds={"parent"}
                  defaultPosition={{
                    x: point.pos.x * size.w! - 20,
                    y: point.pos.y * size.h! - 20,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",

                      zIndex: 3,
                      width: 40,
                      height: 40,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <MapPin
                      className="map-pin"
                      width={"100%"}
                      fill={
                        props.tourData.groups.find(
                          (one: Group) =>
                            one.id ===
                            props.tourData.scenes.find(
                              (one) => one.id === point.target
                            )?.group
                        )?.mainColor || "#fff"
                      }
                      stroke="#222"
                      strokeWidth={18}
                      height={"100%"}
                    />

                    <div
                      className="map-pin-label"
                      style={{
                        // width: "100px",
                        userSelect: "none",
                        position: "absolute",
                        top: "115%",
                        textAlign: "center",
                        maxWidth: 200,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {
                        props.tourData.scenes.find(
                          (one) => one.id === point.target
                        )?.title
                      }
                    </div>
                  </div>
                </Draggable>
              ))}
          </div>
        )}
      </div>
      <div
        style={{
          height: "100%",
          flex: 2,
          maxWidth: 480,
          padding: "1rem",
          boxSizing: "border-box",
          background: "#2221",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <label>
          <h3>{getLanguageFile("overlapping_panel.title")}</h3>
          <input
            type="text"
            value={currentMap?.title}
            onChange={onTitleChange}
          ></input>
        </label>
        <label
          style={{
            display: "block",
            flexDirection: "column",
          }}
        >
          <h3>{getLanguageFile("overlapping_panel.image")}</h3>
          <button
            id="file-upload"
            style={{
              flex: 1,

              width: "100%",
              maxWidth: "100%",
              margin: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
            onClick={() => {
              if (currentMap?.id) {
                setOpenForMap(currentMap.id);
              }
            }}
          >
            <p
              style={{
                margin: 0,
                overflow: "hidden",
                width: "100%",
                textAlign: "left",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {imageList?.find((one) => one.assetId === currentMap?.assetId)
                ?.assetDescription ||
                getLanguageFile("overlapping_panel.no_image")}
            </p>
            <FolderIcon fill="#fff" />
          </button>
        </label>

        <h3>{getLanguageFile("overlapping_panel.scene")}</h3>
        <div
          style={{
            flex: 1,
            overflow: "auto",
          }}
        >
          <div
            style={{
              overflow: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
            {props.tourData.scenes.map((sc) => (
              <SceneElem
                key={sc.id}
                scene={sc}
                groups={props.tourData.groups}
                onClick={() => {
                  if (currentMap?.points?.find((one) => one.target === sc.id))
                    return;
                  else onPinAdd(sc.id as number);
                }}
                disabled={
                  !!currentMap?.points?.find((one) => one.target === sc.id)
                }
                onRemove={onRemove}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const SceneElem = (props: {
  scene: Scene;
  groups: Group[];
  onClick: () => void;
  disabled?: boolean;
  onRemove: (target: number) => void;
}) => {
  const fillingColor = React.useMemo(() => {
    const color = props.groups.find(
      (one: Group) => one.id === props.scene.group
    )?.mainColor;

    return color || "#fff";
  }, [props.groups, props.scene]);

  return (
    <div
      style={{
        background: "#F008",
        borderRadius: ".35rem ",
        userSelect: "none",
        cursor: props.disabled ? "default" : "pointer",
        display: "flex",
        width: "100%",
        height: "50px",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      {props.disabled && (
        <button
          style={{
            minWidth: 0,
            border: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => props.onRemove(props.scene.id as number)}
        >
          <Trashbin />
        </button>
      )}

      <div
        style={{
          padding: 5,
          borderRadius: ".35rem ",
          boxSizing: "border-box",
          width: "100%",
          height: "100%",
          background: "white",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}
        onClick={() => {
          props.onClick();
        }}
      >
        <MapPin
          width={"1.5rem"}
          height={"1.5rem"}
          fill={fillingColor}
          style={{ flexShrink: 0, opacity: props.disabled ? 0.5 : 1 }}
          stroke="#222"
          strokeWidth={20}
        />
        <span
          style={{
            opacity: props.disabled ? 0.5 : 1,
            fontWeight: "600",
            width: "100%",
          }}
        >
          {props.scene.title}
        </span>
      </div>
    </div>
  );
};

// <div
// style={{
//   width: "",
//   display: "flex",
//   alignItems: "center",
//   background: "hotpink",
//   padding: 10,
//   opacity: props.disabled ? 0.4 : 1,
//   borderRadius: ".5rem",
//   userSelect: "none",
//   textDecoration: "underline",
//   textDecorationColor:
//     props.groups.find((one) => one.id === props.scene.group)
//       ?.mainColor || "#222",
// }}
// onClick={props.onClick}
// >
// <MapPin
//   fill={
//     props.groups.find((one) => one.id === props.scene.group)
//       ?.mainColor || "#fff"
//   }
//   stroke="#222"
//   width={"5rem"}
//   height={"5rem"}
//   strokeWidth={22}
// />

// <span
//   style={{
//     fontWeight: 600,
//     overflow: "hidden",
//     whiteSpace: "nowrap",
//     textOverflow: "ellipsis",
//   }}
// >
//   Lorem, ipsum dolor sit amet consectetur adipisicing elit. Iste
//   perspiciatis at, eligendi sed voluptatum sunt perferendis qui
//   voluptatem numquam modi natus eaque? Iste sed expedita ad? Ex, est.
//   Iusto, fugiat?
// </span>
// {/* {props.scene.title} */}
// </div>
